/**
 * Playback Service
 * 
 * Business logic for video playback control and synchronization
 */

import { findBlockById, extractBlockIdFromElement, findBlockElement } from '../../components/TranscriptEditor/utils/blockIdMatcher';
import WordService from './WordService';

/**
 * Playback Service Class
 */
class PlaybackService {
  /**
   * Play from selected text in transcript
   * Finds the block and word that matches the selection, then seeks to that position
   * @param {Object} selection - Text selection object with text and range
   * @param {Array} blocks - Array of transcript blocks
   * @param {Function} seekToBlock - Function to seek to block start
   * @param {Function} seekTo - Function to seek to specific time
   * @param {Function} play - Function to start playback
   * @returns {Promise<Object>} Result of playback operation
   */
  static async playFromSelection(selection, blocks, seekToBlock, seekTo, play) {
    if (!selection || !selection.text || !selection.range) {
      return {
        success: false,
        error: 'Invalid selection',
      };
    }

    try {
      // Find the block element from the selection
      const startNode = selection.range.startContainer;
      const element = startNode.nodeType === Node.TEXT_NODE ? startNode.parentElement : startNode;
      const blockElement = findBlockElement(element);

      if (!blockElement) {
        return {
          success: false,
          error: 'Could not find block element',
        };
      }

      // Extract block ID from element
      const blockId = extractBlockIdFromElement(blockElement.id);
      if (!blockId) {
        return {
          success: false,
          error: 'Could not extract block ID',
        };
      }

      // Find the block in blocks array
      const block = findBlockById(blocks, blockId);
      if (!block) {
        return {
          success: false,
          error: 'Block not found',
        };
      }

      // If no word-level data, play from block start
      if (!WordService.hasWords(block)) {
        await seekToBlock(blockId);
        play();
        return {
          success: true,
          playedFrom: 'block-start',
          blockId,
        };
      }

      // Find matching word
      const match = WordService.findMatchingWord(block, selection.text, selection.range);
      
      if (!match || !match.word) {
        // No matching word found, play from block start
        await seekToBlock(blockId);
        play();
        return {
          success: true,
          playedFrom: 'block-start',
          blockId,
          reason: 'No matching word found',
        };
      }

      // Seek to word start time and play
      if (typeof match.word.start === 'number') {
        await seekTo(match.word.start);
        play();
        return {
          success: true,
          playedFrom: 'word',
          blockId,
          wordIndex: match.index,
          word: WordService.getWordText(match.word),
        };
      } else {
        // Word has no start time, fallback to block start
        await seekToBlock(blockId);
        play();
        return {
          success: true,
          playedFrom: 'block-start',
          blockId,
          reason: 'Word has no start time',
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Error in playFromSelection',
      };
    }
  }
}

export default PlaybackService;

